import { FolderIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Box, Card, CardContent, IconButton, Tooltip, useTheme } from '@mui/material';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import stylesProjectPage from '../../styles/projectPage/index.module.scss';
import { useSnackbar } from '../../utils/useSnackbar';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import DEFAULT_ROUTES from '../../constants/routes';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useDispatch } from 'react-redux';
import { selectProject } from '../../features/projects/ProjectSlice';
import { resetAllState } from '../../features/replayslice';
import { reset as resetQueryState } from '../../features/query/querySlice';
dayjs.extend(relativeTime);

const DURATION = 100;
const ProjectCard = ({ isNew, index=0, onEdit=DO_NOTHING, onDelete=DO_NOTHING, project={}, selectedProject={} }) => {
    const { showSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const theme = useTheme();
    const currentProject = useMemo(() => selectedProject?.id === project.id, [selectedProject, project]);
    const dispatch = useDispatch();
    
    const AnimatedProjectCard = useCallback(({ children, delay, isNew = false }) => {
        const [show, setShow] = useState(false);
        useEffect(() => {
            if (isNew) { setShow(true) } 
            else {
                const timer = setTimeout(() => setShow(true), delay);
                return () => clearTimeout(timer);
            }
        }, [delay, isNew]);
        
        return (
            <Box
                sx={{
                    opacity: show ? 1 : 0,
                    transform: show ? 'translateY(0px)' : 'translateY(20px)',
                    transition: isNew  ? 'opacity 0.4s ease, transform 0.4s ease'  : 'opacity 0.5s ease, transform 0.5s ease',
                    animation: isNew && show ? 'slideInFromTop 0.4s ease' : 'none',
                }}    
            >
                {children}
            </Box>
        );    
    }, []);

    const handleProjectClick = useCallback(async (e, project={}) => {
        e.preventDefault();
        e.stopPropagation();
        dispatch(selectProject(project || {}));
        dispatch(resetAllState());
        dispatch(resetQueryState());
        navigate(`${DEFAULT_ROUTES.BASE_URL}${DEFAULT_ROUTES.PROJECT}/${project.id}/test-cases`);
    }, [navigate, dispatch]);

    const getCreatedDisplay = useCallback((created_at) => {
        const created = dayjs(created_at);
        const now = dayjs();
        const diffSeconds = now.diff(created, 'second');
        let createdDisplay = '';
        if (diffSeconds < 60) createdDisplay = 'Just now';
        else if (diffSeconds < 3600 * 24) createdDisplay = created.fromNow();
        else createdDisplay = created.format('MMM D, YYYY, h:mm A');
        return createdDisplay;
    }, []);

    const createdDisplay = useMemo(() => getCreatedDisplay(project.created_at), [getCreatedDisplay, project.created_at]);

    return (
        <AnimatedProjectCard isNew={isNew} delay={index * DURATION}>
            <Card 
                className={`${stylesProjectPage.card} ${currentProject ? 'bg-slate100' : 'bg-white'}`}  
                onClick={(e) => handleProjectClick(e, project)} 
                role="button" 
                tabIndex={0}
                raised={currentProject}
                sx={{
                    border: currentProject ? "2px solid": "1px solid",
                    borderColor: "divider",
                    backgroundColor: currentProject ? theme.palette.customColor.slate[100] : "white",
                    width: '100%',
                    minWidth: 0
                }}
            >
                <CardContent className={stylesProjectPage['cardWrapper']}>
                    <Box className={stylesProjectPage['iconContainer']}>
                        <Tooltip title="Edit Project">
                            <IconButton size="small" onClick={(e) => onEdit(e, project)}>
                                <PencilIcon className="icon" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Project">
                            <IconButton size="small" onClick={(e) => onDelete(e, project.id)}>
                                <TrashIcon className="icon" />
                            </IconButton>
                        </Tooltip>
                    </Box>
                    <Box className={stylesProjectPage['cardContent']}>
                        <FolderIcon className="icon-xl primary" />
                        <Tooltip title={project.name}>
                            <p className="ellipsis-container-line-2">{project.name}</p>
                        </Tooltip>
                    </Box>
                    <Box className={stylesProjectPage['createdDisplay']}>
                        <p className={createdDisplay === 'Just now' ?  stylesProjectPage.justNow : ""}>
                            {createdDisplay}
                        </p>
                    </Box>
                </CardContent>
            </Card>
        </AnimatedProjectCard>
    );
};

export default React.memo(ProjectCard);
